/**
 * distribuicao.ts — S1 Bloco A (multi-cota) · helpers do frontend
 * Espelha worker/src/lib/distribuicao.ts (regras devem andar juntas).
 */

import type { Demanda, DistribuicaoCredito } from './types';

export function parseCreditos(json?: string | null): DistribuicaoCredito[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json) as unknown;
    return Array.isArray(v) ? (v as DistribuicaoCredito[]) : [];
  } catch {
    return [];
  }
}

/** Card exige vínculo quando é entrega contratual de produção. */
export function exigeVinculoDistribuicao(
  d: Pick<Demanda, 'origem' | 'tipoEntrega' | 'tipoDemanda'>,
): boolean {
  if (d.tipoDemanda === 'card_mae' || d.tipoDemanda === 'verificacao_mac') return false;
  return d.origem === 'Externo (contratual)' && (d.tipoEntrega ?? 'producao') === 'producao';
}

/** Motivo pelo qual a conclusão está travada, ou null se pode publicar. */
export function validarTravaDistribuicao(
  d: Pick<Demanda, 'origem' | 'tipoEntrega' | 'tipoDemanda' | 'contratoId' | 'contratosIds' | 'distribuicaoConfirmada'>,
): string | null {
  if (!exigeVinculoDistribuicao(d)) return null;
  const temVinculo = (d.contratosIds?.length ?? 0) > 0 || !!d.contratoId;
  if (!temVinculo) {
    return 'Distribuição vazia: vincule ao menos um contrato antes de concluir esta entrega.';
  }
  if (!d.distribuicaoConfirmada) {
    return 'Distribuição não confirmada: Comercial ou Coordenação precisa confirmar antes da conclusão.';
  }
  return null;
}

/** Cliente (sid) enxerga a demanda? Solicitante original OU lista de distribuição. */
export function demandaDoCliente(
  d: Pick<Demanda, 'solicitanteId' | 'clientesIds'>,
  sid: string,
): boolean {
  return d.solicitanteId === sid || (d.clientesIds ?? []).includes(sid);
}

/** Demandas ativas que exigem vínculo mas estão com distribuição vazia. */
export function pendentesDeDistribuicao(demandas: Demanda[]): Demanda[] {
  const ativos = ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'];
  return demandas.filter(
    (d) =>
      ativos.includes(d.status) &&
      exigeVinculoDistribuicao(d) &&
      (d.contratosIds?.length ?? 0) === 0 &&
      !d.contratoId,
  );
}

/** Publicadas com crédito aguardando confirmação do Comercial. */
export function aguardandoConfirmacaoCredito(demandas: Demanda[]): Demanda[] {
  return demandas.filter(
    (d) =>
      d.status === 'Publicada' &&
      parseCreditos(d.distribuicaoCreditos).some((c) => c.status === 'aguardando'),
  );
}

/** Meta por contrato: nº de cards Publicados com crédito CONFIRMADO no contrato. */
export function publicadasComCreditoPorContrato(demandas: Demanda[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const d of demandas) {
    if (d.status !== 'Publicada') continue;
    const creditos = parseCreditos(d.distribuicaoCreditos);
    if (creditos.length > 0) {
      for (const c of creditos) {
        if (c.status === 'confirmado') m.set(c.contratoId, (m.get(c.contratoId) ?? 0) + 1);
      }
    } else if (!exigeVinculoDistribuicao(d)) {
      // fora da trava (legado/interno): conta pelo vínculo direto
      for (const cid of d.contratosIds ?? (d.contratoId ? [d.contratoId] : [])) {
        m.set(cid, (m.get(cid) ?? 0) + 1);
      }
    }
  }
  return m;
}
